import { useEffect, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import {
  useBoardId,
  useEnterpriseId,
  useWorkspaceId,
} from '@trello/id-context';
import { Button } from '@trello/nachos/button';

import styles from './LandingScreen.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import emphasis from 'resources/images/split-screen-onboarding/trello-icon-emphasis.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import trelloLogoLight from 'resources/images/split-screen-onboarding/trello-logo-light.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import underline from 'resources/images/split-screen-onboarding/underline.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import watchVideoImage from 'resources/images/split-screen-onboarding/watch-video.svg';

interface LandingScreenProps {
  onVideoButtonClick: () => void;
  onDiscoveryButtonClick: () => void;
}

export const LandingScreen: FunctionComponent<LandingScreenProps> = ({
  onDiscoveryButtonClick,
  onVideoButtonClick,
}) => {
  const boardId = useBoardId();
  const workspaceId = useWorkspaceId();
  const enterpriseId = useEnterpriseId();

  const { value: videoLinkEnabled } = useFeatureGate(
    'xf_splitscreen_onboarding_video',
  );
  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'splitScreenOnboardingLandingScreen',
      containers: formatContainers({ boardId, workspaceId, enterpriseId }),
    });
  }, [boardId, workspaceId, enterpriseId]);

  const onVideoClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'watchVideoButton',
      source: 'splitScreenOnboardingLandingScreen',
      containers: formatContainers({ boardId, workspaceId, enterpriseId }),
    });

    onVideoButtonClick();
  };

  const onDiscoveryClick = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'meetNewTrelloButton',
      source: 'splitScreenOnboardingLandingScreen',
      containers: formatContainers({ boardId, workspaceId, enterpriseId }),
    });

    onDiscoveryButtonClick();
  };

  return (
    <div className={styles.container}>
      <div className={styles.logoContainer}>
        <img src={emphasis} alt="emphasis" className={styles.emphasis} />
        <img src={trelloLogoLight} alt="Trello Logo" width={200} height={200} />
      </div>
      <div className={styles.titleContainer}>
        <h1>
          <FormattedMessage
            id="templates.split_screen.onboarding.landing-screen.title"
            defaultMessage="Welcome to the new Trello"
            description="Landing screen title"
          />
        </h1>
        <img src={underline} alt="underline" className={styles.underline} />
      </div>
      <p>
        <FormattedMessage
          id="templates.split_screen.onboarding.landing-screen.sub-title"
          defaultMessage="Brand-new ways to capture, organize, and schedule your to-dos."
          description="Landing screen sub title"
        />
      </p>
      <Button
        iconAfter={<ArrowRightIcon size="medium" label="" />}
        className={styles.discoveryButton}
        onClick={onDiscoveryClick}
      >
        <FormattedMessage
          id="templates.split_screen.onboarding.landing-screen.quick-intro"
          defaultMessage="Get a quick introduction"
          description="Landing screen discovery button"
        />
      </Button>
      {videoLinkEnabled && (
        <Button
          appearance="subtle"
          className={styles.videoButton}
          onClick={onVideoClick}
        >
          <img src={watchVideoImage} alt="play icon" />
          <FormattedMessage
            id="templates.split_screen.onboarding.landing-screen.video-cta"
            defaultMessage="Watch video"
            description="Landing screen video button"
          />
        </Button>
      )}
    </div>
  );
};
